import * as React from 'react';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import { useQuery } from '../hooks/useQuery';
import Loading from '../common/Loading';
import { useMutation } from '../hooks/useMutation';
import { toastr } from 'app/common/toastr';
import RequestOwnership from './request-ownership';
import OwnershipReceived from './ownership-received';
import MoveToAdmin from './move-to-admin';
import RefundPayment from './refundPayment';
import Timeline from 'app/activity/timeline';

import { connect } from 'react-redux';
import { activities as fetchActivities } from 'app/redux/modules/dwelling';
import { bbrUser as fetchBbrUser } from 'app/redux/modules/bbr';

const ADMIN_OPTIONS = `
query AdminOptions($dwellingId: ID!) {
    listings(dwellingId: $dwellingId) {
        transaction
        product
        currency
        amount
        status
        capturedAt
        refundedAt
        refundedTransaction
        refund
        reason
    }
}`;

const SEND_RECEIPT_MAIL = `mutation sendReceiptMail($userId: ID!, $dwellingId: ID!, $transactionId: ID!) { sendReceiptMail(userId: $userId, dwellingId: $dwellingId, transactionId: $transactionId) }`;

const REFUND_PAYMENT = `mutation refundPayment($dwelling: String!, $paymentId: String!, $amount: Int!, $reason: String!) { refundPayment(dwelling: $dwelling, paymentId: $paymentId, amount: $amount, reason: $reason) { 
  transaction
  product
  currency
  amount
  status
  capturedAt
  refundedAt
  refundedTransaction
  refund
  reason } }`;

const UPDATE_AI_DESCRIPTION = `
mutation updateAIDescription($dwelling: String!) {
  updateAIDescription(dwelling: $dwelling) {
    id
    description
  }
}
`;

const UPDATE_IMAGE_VALIDATION = `
mutation updateImageValidation($dwelling: String!) {
  updateImageValidation(dwelling: $dwelling) {
    id
    images
  }
}
`;

const AdminPanel = (props) => {
  const [aiLoading, setAiLoading] = React.useState(false);
  const [imageValidationLoading, setImageValidationLoading] =
    React.useState(false);

  React.useEffect(() => {
    if (props.dwelling && props.dwelling.uuid) {
      props.fetchActivities(props.dwelling.uuid);
    }
  }, [props.dwelling && props.dwelling.uuid]);

  React.useEffect(() => {
    if (props.dwelling && props.dwelling.user) {
      props.fetchBbrUser(props.dwelling.user);
    }
  }, [props.dwelling && props.dwelling.user]);

  const { data, errors, loading, refetch } = useQuery(ADMIN_OPTIONS, {
    variables: { dwellingId: props.dwelling.uuid }
  });

  const { mutate } = useMutation(SEND_RECEIPT_MAIL);
  const { mutate: mutateRefund } = useMutation(REFUND_PAYMENT);
  const { mutate: mutateAIDescription } = useMutation(UPDATE_AI_DESCRIPTION);
  const { mutate: mutateImageValidation } = useMutation(
    UPDATE_IMAGE_VALIDATION
  );

  const refreshDwellingView = () => {
    window.location.reload();
  };

  const sendReciept = async (transactionId) => {
    const { data } = await mutate({
      userId: props.dwelling.user,
      dwellingId: props.dwelling.uuid,
      transactionId
    });

    if (data && data.sendReceiptMail === true) {
      toastr.success('Kvitteing sendt');
    }
  };

  const updateAIDescription = async () => {
    try {
      setAiLoading(true);
      const { data } = await mutateAIDescription({
        dwelling: props.dwelling.uuid
      });

      if (data && data.updateAIDescription) {
        toastr.success('AI beskrivelse opdateret');
        refreshDwellingView();
      } else {
        toastr.error('Kunne ikke opdatere AI beskrivelse');
      }
    } catch (err) {
      toastr.error('Kunne ikke opdatere AI beskrivelse');
    } finally {
      setAiLoading(false);
    }
  };

  const updateImageValidation = async () => {
    try {
      setImageValidationLoading(true);
      const { data } = await mutateImageValidation({
        dwelling: props.dwelling.uuid
      });

      if (data && data.updateImageValidation) {
        toastr.success('Billeder valideret');
        refreshDwellingView();
      } else {
        toastr.error('Kunne ikke validere billeder');
      }
    } catch (err) {
      toastr.error('Kunne ikke validere billeder');
    } finally {
      setImageValidationLoading(false);
    }
  };

  const onRequestSubmit = (message, errorMessage) => {
    return async (res) => {
      if (res) {
        toastr.success(message || 'Anmodning sendt');
        refreshDwellingView();
      } else {
        toastr.error(errorMessage || 'Noget gik galt - kontakt team');
      }
    };
  };

  const actionButtonProps = {
    primary: true,
    style: { width: '100%', justifyContent: 'center' }
  };

  const refundPayment = async (paymentId, reason, amount) => {
    if (amount > 0 && reason && paymentId) {
      const { data } = await mutateRefund({
        dwelling: props.dwelling.uuid,
        paymentId,
        amount: Number(amount),
        reason
      });

      if (data && data.refundPayment) {
        toastr.success('Refundering sendt');
        refetch();
      }
    } else {
      toastr.error(
        'Missing data',
        `Payment: ${paymentId}, Amount: ${amount}, Reason: ${reason}`
      );
    }
  };

  const MS_48H = 48 * 60 * 60 * 1000;
  const now = Date.now();

  const recentActivities = (props.activities || [])
    .filter(function (a) {
      if (!a) {
        return false;
      }

      var ts = a.timestamp;
      if (!ts) {
        return false;
      }

      var t = Date.parse(ts);
      if (isNaN(t)) {
        return false;
      }

      return t >= now - MS_48H;
    })
    .sort(function (a, b) {
      return Date.parse(b.timestamp) - Date.parse(a.timestamp);
    });

  var bbr = props.bbrData;
  var bbrName = bbr && bbr.name ? bbr.name : '';
  var bbrPhone = bbr && bbr.phoneNumber ? bbr.phoneNumber : '';
  var bbrStreet =
    bbr && bbr.address && bbr.address.street ? bbr.address.street : '';
  var bbrPostal =
    bbr && bbr.address && bbr.address.postal ? bbr.address.postal : '';

  return (
    <div>
      <Card style={{ padding: '1em', width: '450px' }}>
        <h2
          style={{ margin: '0px', paddingTop: '15px', paddingBottom: '15px' }}
        >
          Admin Panel
        </h2>

        <h3 style={{ margin: '0px' }}>Actions</h3>

        {props.dwelling && props.dwelling.uuid ? (
          <div
            style={{
              paddingTop: '15px',
              paddingBottom: '15px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8
            }}
          >
            <RequestOwnership
              confirmation
              dwelling={props.dwelling.uuid}
              onSubmit={onRequestSubmit()}
              buttonProps={actionButtonProps}
            />

            {['DOCUMENTATION_REQUESTED'].includes(props.dwelling.status) ? (
              <OwnershipReceived
                confirmation
                dwelling={props.dwelling.uuid}
                onSubmit={onRequestSubmit('Status opdateret')}
                buttonProps={actionButtonProps}
              />
            ) : null}

            <MoveToAdmin
              confirmation
              dwelling={props.dwelling.uuid}
              onSubmit={onRequestSubmit(
                'Lejemålet flyttet til deleted@kodyl.com'
              )}
              buttonProps={actionButtonProps}
            />

            <Button
              variant="contained"
              color="primary"
              onClick={updateAIDescription}
              disabled={aiLoading}
              style={{ width: '100%' }}
            >
              {aiLoading
                ? 'Updating AI beskrivelse...'
                : 'Update AI Description'}
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={updateImageValidation}
              disabled={imageValidationLoading}
              style={{ width: '100%' }}
            >
              {imageValidationLoading
                ? 'Validating Images...'
                : 'Validate Images'}
            </Button>
          </div>
        ) : (
          <p>No actions currently</p>
        )}

        {errors ? (
          <span style={{ color: 'red' }}>error</span>
        ) : loading ? (
          <Loading />
        ) : (
          <>
            <h3 style={{ margin: '0px' }}>Listings</h3>
            <table style={{ width: '100%' }}>
              <tbody>
                {data &&
                  data.listings &&
                  data.listings.map((listing) => {
                    return (
                      <tr
                        key={listing.transaction}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}
                      >
                        <td style={{ width: '45%', alignContent: 'center' }}>
                          {`${listing.product} (${listing.status})`}
                          <div style={{ fontSize: '0.85em', color: '#666' }}>
                            {typeof listing.amount === 'number'
                              ? `${(listing.amount / 100).toLocaleString(
                                  'da-DK',
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  }
                                )} ${listing.currency || ''}`
                              : '-'}
                          </div>
                          <div style={{ fontSize: '0.85em', color: '#666' }}>
                            {listing.capturedAt
                              ? new Date(listing.capturedAt).toLocaleString(
                                  'da-DK'
                                )
                              : '-'}
                          </div>
                        </td>
                        <td style={{ width: '30%', alignContent: 'center' }}>
                          {(listing.status === 'CAPTURED' ||
                            listing.status === 'REFUNDED') && (
                            <Button
                              onClick={() => sendReciept(listing.transaction)}
                            >
                              Gensend kvittering
                            </Button>
                          )}
                        </td>
                        <td style={{ width: '25%', alignContent: 'center' }}>
                          <RefundPayment
                            confirmation
                            payment={listing}
                            onSubmit={refundPayment}
                          />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </>
        )}
      </Card>

      <Card style={{ padding: '1em', width: '450px', marginTop: 16 }}>
        <h3 style={{ margin: 0, paddingTop: 15, paddingBottom: 15 }}>
          User - BBR Information
        </h3>

        {props.bbrLoading ? (
          <Loading />
        ) : bbr ? (
          <div style={{ lineHeight: 1.6 }}>
            <div>
              <strong>Name:</strong> {bbrName || '-'}
            </div>
            <div>
              <strong>Phone:</strong> {bbrPhone || '-'}
            </div>
            <div>
              <strong>Street:</strong> {bbrStreet || '-'}
            </div>
            <div>
              <strong>Postal:</strong> {bbrPostal || '-'}
            </div>
          </div>
        ) : (
          'Has no information'
        )}
      </Card>

      <Card style={{ padding: '1em', width: '450px', marginTop: 16 }}>
        <h3 style={{ margin: 0, paddingTop: 15, paddingBottom: 15 }}>
          Logs - Latest 48 hours
        </h3>

        {props.activitiesLoading ? (
          <Loading />
        ) : recentActivities.length ? (
          <Timeline data={recentActivities} />
        ) : (
          'No logs in the last 48 hours'
        )}
      </Card>
    </div>
  );
};

function mapStateToProps(state) {
  var dwellingState = state.dwelling;
  var activities = null;
  var activitiesLoading = false;

  if (dwellingState && typeof dwellingState.get === 'function') {
    activities = dwellingState.get('activities');
    activitiesLoading = dwellingState.get('loading');

    if (activities && typeof activities.toJS === 'function') {
      activities = activities.toJS();
    }
  } else if (dwellingState) {
    activities = dwellingState.activities;
    activitiesLoading = dwellingState.loading;
  }

  var bbrState = state.bbr;
  var bbrData = null;
  var bbrLoading = false;

  if (bbrState) {
    bbrData = bbrState.data;
    bbrLoading = bbrState.loading;
  }

  return {
    activities,
    activitiesLoading,
    bbrData,
    bbrLoading
  };
}

export default connect(mapStateToProps, {
  fetchActivities,
  fetchBbrUser
})(AdminPanel);